import { createContext, useReducer, useEffect, useState, useCallback } from 'react';
import type { FC } from 'react';
import { ActivityType } from 'src/types/activity';
import crmApi from 'src/api/CrmApi';
import useAuth from 'src/hooks/useAuth';
import { ContextProviderProps } from 'src/types/contextProviderProps';
import { Attachment, CRMActivityEntity } from 'src/types/CRMActivityEntity';
import { ActivityFormState, initialFormState } from 'src/types/activityFormState';
import useSnackbar from 'src/hooks/useSnackbar';
import useCustomer from 'src/hooks/useCustomer';
import { Customer } from 'src/types/customer';
import { UserSimple } from 'src/types/userSimple';
import { activityFormReducer } from './CrmContextFunctions';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';

export interface ICrmContext {
  editingActivityId: number | null;
  setEditingActivityId: (activityId: number | null) => void;
  /**
   * The state of the activity form. Used for creating and editing activities in the UI.
   */
  activityFormState: ActivityFormState;

  /**
   * all possible activity types
   */
  activityTypes: ActivityType[];

  /**
   * Sets the flag used by the form to determine if it should be open or not.
   * @param openActivityForm
   */
  setIsFormOpen: (openActivityForm: boolean) => void;
  // isFormOpen: boolean;

  /**
   * Called when the user clicks the add activity button.
   */
  addActivityClicked: () => void;

  /**
   * User clicks the edit button. The context will pull fresh value of crm activity from the api and set it as the activity being edited.
   * @param activityId
   */
  editActivityClicked: (activityId: number) => void;

  /**
   * The user has clicked the delete button. Open the delete dialog.
   */
  deleteActivityClicked: (activityId: number) => void;

  /**
   * Sets the activity that the form is currently editing. All changes to the form will be made to this activity.
   * @param activity
   */
  setFormActivity: (activity: CRMActivityEntity) => void;

  /**
   * The customer that the activity is being created for. Seperate from customer as this is specific to the activity form and can be changed independently.
   */
  formCustomer: Customer;

  setFormCustomer: (customer: Customer) => void;

  /**
   * Cancel Without Saving. Reset the form to initial state.
   */
  cancelWithoutSaving: () => void;

  /**
   * All sfa users.  Is on a 2 hour cache in local storage.
   */
  allSFAUsers: UserSimple[];

  /**
   * All crm users. Is on a 2 hour cache in local storage.
   */
  allCRMUsers: UserSimple[];

  /**
   * flag to open the delete activity dialog
   */
  deleteActivityOpen: boolean;

  /**
   * Sets the flag to open the delete activity dialog
   */
  setDeleteActivityOpen: (open: boolean) => void;

  /**
   * The activity that is being deleted.
   */
  activityToDelete: CRMActivityEntity | null;

  /**
   * Sets the activity that is being deleted.
   */
  setActivityToDelete: (activity: CRMActivityEntity | null) => void;

  /**
   * Function to delete the activity from the db and set state after.
   */
  deleteActivity: () => void;

  /**
   * Flag to determine if the grid should reload
   */
  shouldReloadGrid: boolean;

  /**
   * Sets the flag to determine if the grid should reload
   */
  setShouldReloadGrid: (reload: boolean) => void;

  /**
   * The user has clicked the attachment chip. Download the document.
   */
  attachmentClicked: (attachment: Attachment) => void;

  /**
   * if this is set, the activityId in the grid cell details will reload
   */
  reloadCellWithThisActivityId: number | null;

  /**
   * Sets the reloadCellWithThisActivityId set to null to stop reloading
   */
  setReloadCellWithThisActivityId: (activityId: number | null) => void;

  /**
   * Fetches activity types for dropdown
   */
  getAllActivityTypes: () => void;

  /**
   * Sets the activity filters for the grid
   * @param filters
   */
  setActivityFilters: (filters: CompositeFilterDescriptor[]) => void;

  /**
   * The activity filter dropdowns for the grid
   */
  activityFilters: CompositeFilterDescriptor[];

  /**
   * Currently only sets 'Pinned' btn
   * @param filters
   */
  setFilterBtnsVal: (value: string) => void;

  /**
   * 'Pinned' btn value
   */
  filterBtnsVal: string;

  /**
   * Sets 'Complete' and 'Incomplete' btn values
   */
  setFilterBtnsValComplete: (value: string) => void;

  /**
   * 'Complete' and 'Incomplete' btn values
   */
  filterBtnsValComplete: string;

  /**
   * Sets the flag to determine if the activities are the user's or all activities
   */
  setIsMyActivities: (isMyActivities: boolean) => void;

  /**
   * Flag to determine if the activities are the user's or all activities
   */
  isMyActivities: boolean;
}

const CrmContext = createContext<ICrmContext>({
  editingActivityId: null,
  setEditingActivityId: () => {},
  setIsFormOpen: () => {},
  activityTypes: [],
  activityFormState: { ...initialFormState },
  addActivityClicked: () => {},
  editActivityClicked: () => {},
  setFormActivity: () => {},
  formCustomer: null,
  setFormCustomer: () => {},
  cancelWithoutSaving: () => {},
  allSFAUsers: [],
  allCRMUsers: [],
  deleteActivityOpen: false,
  deleteActivityClicked: () => {},
  setDeleteActivityOpen: () => {},
  activityToDelete: null,
  setActivityToDelete: () => {},
  deleteActivity: () => {},
  shouldReloadGrid: false,
  setShouldReloadGrid: () => {},
  attachmentClicked: () => {},
  reloadCellWithThisActivityId: null,
  setReloadCellWithThisActivityId: () => {},
  getAllActivityTypes: () => {},
  setActivityFilters: () => {},
  activityFilters: [],
  setFilterBtnsVal: () => {},
  filterBtnsVal: '',
  setFilterBtnsValComplete: () => {},
  filterBtnsValComplete: '',
  setIsMyActivities: () => {},
  isMyActivities: true,
});

export const CrmProvider: FC<ContextProviderProps> = (props) => {
  //Reducer is for the activity form state. This is used to create and edit activities. It is what fills in the form.
  const [activityFormState, dispatch] = useReducer(activityFormReducer, { ...initialFormState });

  const { openSnackBar } = useSnackbar();
  const { user } = useAuth();
  const { customer } = useCustomer();
  const [formCustomer, setFormCustomer] = useState<Customer | null>({ ...customer });
  const [deleteActivityOpen, setDeleteActivityOpen] = useState(false);
  const [activityToDelete, setActivityToDelete] = useState<CRMActivityEntity | null>(null);
  const [allSFAUsers, setAllSFAUsers] = useState<UserSimple[]>([]);
  const [allCRMUsers, setAllCRMUsers] = useState<UserSimple[]>([]);
  const [shouldReloadGrid, setShouldReloadGrid] = useState(false);
  const [reloadCellWithThisActivityId, setReloadCellWithThisActivityId] = useState<number | null>(null);
  const [editingActivityId, setEditingActivityId] = useState<number | null>(null);
  const [activityFilters, setActivityFilters] = useState<CompositeFilterDescriptor[]>([]);
  const [filterBtnsVal, setFilterBtnsVal] = useState('');
  const [filterBtnsValComplete, setFilterBtnsValComplete] = useState('');
  const [isMyActivities, setIsMyActivities] = useState(true);

  //For Activity form used for notifications
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);

  useEffect(() => {
    if (user) {
      crmApi.getAllSFAUsers().then((res) => {
        setAllSFAUsers(res);
      });

      crmApi.getCrmUsers().then((res) => {
        setAllCRMUsers(res);
      });
    }
  }, [user]);

  const getAllActivityTypes = async () => {
    if (activityTypes.length > 0) return;
    try {
      const res = await crmApi.getAllActivityTypes();
      setActivityTypes(res);
    } catch (err) {
      console.error('CrmContext: getAllActivityTypes', err);
    }
  };

  const setIsFormOpen = (isOpen: boolean) => {
    dispatch({ type: 'SetIsFormOpen', payload: isOpen });
  };

  const addActivityClicked = async () => {
    dispatch({ type: 'AddActivityClicked' });
  };

  const editActivityClicked = (activityId: number) => {
    setEditingActivityId(activityId);
    setIsFormOpen(true);
  };

  const setFormActivity = (activity: CRMActivityEntity) => {
    dispatch({ type: 'setFormActivity', payload: activity });
  };

  const deleteActivityClicked = (activityId: number) => {
    crmApi
      .getCrmActivity(activityId)
      .then((res) => {
        let crmActivity = res as CRMActivityEntity;
        setActivityToDelete(crmActivity);
        setDeleteActivityOpen(true);
      })
      .catch((err) => {
        openSnackBar(`Error: ${err}`, 'error', 10000);
        console.error('Error: ', err);
      });
  };

  const deleteActivity = () => {
    crmApi
      .deleteActivity(activityToDelete?.activityId || 0)
      .then(() => {
        openSnackBar('Deleted Activity', 'success', 2000);
        setShouldReloadGrid(true);
      })
      .catch((err) => {
        openSnackBar(`Error deleting activity: ${err}`, 'error', 10000);
        console.error(err, 'error on delete activity');
      });
  };

  const attachmentClicked = async (attachment: Attachment) => {
    const response = await crmApi.downloadAttachment(attachment);
    Object.assign(document.createElement('a'), {
      download: attachment.name,
      href: `${attachment.fileType},${response}`,
    }).click();
  };

  const cancelWithoutSaving = () => {
    setEditingActivityId(null);
    dispatch({ type: 'cancelWithoutSaving', payload: null });
  };

  const contextValue = {
    editingActivityId,
    setEditingActivityId,
    activityTypes,
    setIsFormOpen,
    activityFormState,
    addActivityClicked,
    editActivityClicked,
    deleteActivityClicked,
    setFormActivity,
    formCustomer,
    setFormCustomer,
    cancelWithoutSaving,
    allSFAUsers,
    allCRMUsers,
    deleteActivityOpen,
    setDeleteActivityOpen,
    activityToDelete,
    setActivityToDelete,
    deleteActivity,
    shouldReloadGrid,
    setShouldReloadGrid,
    attachmentClicked,
    reloadCellWithThisActivityId,
    setReloadCellWithThisActivityId,
    getAllActivityTypes,
    setActivityFilters,
    activityFilters,
    setFilterBtnsVal,
    filterBtnsVal,
    setFilterBtnsValComplete,
    filterBtnsValComplete,
    setIsMyActivities,
    isMyActivities,
  };

  return <CrmContext.Provider value={contextValue}>{props.children}</CrmContext.Provider>;
};

export default CrmContext;
